import React, { useEffect, useRef } from 'react';
import { FiX, FiPlay, FiBook, FiVideo } from 'react-icons/fi';
import { normalizeImageUrl } from '../../utils/imageUtils';

// Importando as imagens padrão
import defaultVideoThumb from '../../assets/images/thumbnails/thumb-video.webp';
import defaultBookThumb from '../../assets/images/thumbnails/thumb-livro.webp';
import defaultMagazineThumb from '../../assets/images/thumbnails/thumb-revista.webp';

interface Material {
  id: string;
  titulo: string;
  descricao: string;
  tipo: 'vídeo' | 'livro' | 'revista';
  iframe: string;
  thumb_url: string | null;
}

interface MaterialViewModalProps {
  material: Material | null;
  showContent: boolean;
  onClose: () => void;
  onPlayContent: () => void;
}

const MaterialViewModal: React.FC<MaterialViewModalProps> = ({
  material,
  showContent,
  onClose,
  onPlayContent
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    // Fechar modal ao pressionar ESC
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    window.addEventListener('keydown', handleEsc);
    
    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [onClose]);
  
  // Fechar ao clicar fora do modal
  const handleOutsideClick = (e: React.MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
      onClose();
    }
  };
  
  // Limpa a string de iframe para evitar problemas de segurança
  const sanitizeIframe = (iframeStr: string): string => {
    // Remove scripts e eventos onload/onerror, mantém apenas src seguras
    return iframeStr
      .replace(/on\w+="[^"]*"/g, '')
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  };

  // Melhora a exibição do iframe do FlipHTML5
  const enhanceFlipHTML5Iframe = (iframeStr: string): string => {
    // Primeiro sanitiza o iframe
    let enhancedIframe = sanitizeIframe(iframeStr);
    
    // Modifica os atributos para melhorar a exibição
    enhancedIframe = enhancedIframe
      .replace(/width="[^"]*"/g, 'width="100%"')
      .replace(/height="[^"]*"/g, 'height="100%"')
      .replace(/style="[^"]*"/g, 'style="width:100%; height:100%; border:0; margin:0; padding:0;"');
    
    return enhancedIframe;
  };

  // Obter a imagem padrão apropriada baseada no tipo de material
  const getDefaultThumbnail = (tipo: string) => {
    switch (tipo) {
      case 'vídeo':
        return defaultVideoThumb;
      case 'livro':
        return defaultBookThumb;
      case 'revista':
        return defaultMagazineThumb;
      default:
        return defaultVideoThumb;
    }
  };
  
  if (!material) return null;
  
  // Se estiver no modo de visualização de conteúdo
  if (showContent) {
    // Detectar o tipo de conteúdo
    const isFlipHTML5 = material.iframe.includes('fliphtml5.com');
    const isVideo = material.tipo === 'vídeo' || 
                    material.iframe.includes('youtube.com') || 
                    material.iframe.includes('vimeo.com');
    
    return (
      <div 
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/95"
        onClick={onClose}
      >
        <div className="absolute top-4 right-4 z-50">
          <button
            onClick={onClose}
            className="bg-red-600 hover:bg-red-700 text-white rounded-full p-2 transition-colors"
            aria-label="Fechar"
          >
            <FiX size={20} />
          </button>
        </div>
        
        {isFlipHTML5 ? (
          <div className="w-full h-full flex items-center justify-center">
            <div 
              className="w-full h-[90vh] max-w-[95vw]"
              dangerouslySetInnerHTML={{ 
                __html: enhanceFlipHTML5Iframe(material.iframe)
              }}
            />
          </div>
        ) : isVideo ? (
          <div className="w-full h-full flex items-center justify-center">
            <div 
              className="w-full max-w-[80vw]"
              style={{ 
                maxHeight: '80vh',
                maxWidth: '1400px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              <div 
                className="w-full aspect-w-16 aspect-h-9 bg-black rounded-lg overflow-hidden"
                style={{ margin: 'auto' }}
                dangerouslySetInnerHTML={{ __html: sanitizeIframe(material.iframe) }}
              />
            </div>
          </div>
        ) : (
          <div className="w-full max-w-5xl relative flex items-center justify-center" style={{ maxWidth: '1400px' }}>
            <div 
              className="aspect-w-16 aspect-h-9 bg-black rounded-lg overflow-hidden w-full"
              dangerouslySetInnerHTML={{ __html: sanitizeIframe(material.iframe) }}
            />
          </div>
        )}
      </div>
    );
  }
  
  // Modo de visualização de detalhes
  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={handleOutsideClick}
    >
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm"></div>
      
      <div 
        ref={modalRef}
        className="bg-gray-900 rounded-lg max-w-4xl w-full shadow-xl relative z-10 overflow-hidden animate-modal-appear"
      >
        {/* Cabeçalho com botão de fechar */}
        <div className="absolute top-4 right-4 z-10">
          <button
            onClick={onClose}
            className="bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
            aria-label="Fechar"
          >
            <FiX size={20} />
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2">
          {/* Imagem / Thumbnail */}
          <div className="relative">
            <div className="aspect-w-1 aspect-h-1 md:aspect-h-auto md:h-full">
              {material.thumb_url ? (
                <img 
                  src={normalizeImageUrl(material.thumb_url) || getDefaultThumbnail(material.tipo)} 
                  alt={material.titulo} 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    console.log('Erro ao carregar imagem:', material.thumb_url);
                    (e.target as HTMLImageElement).src = getDefaultThumbnail(material.tipo);
                  }}
                />
              ) : (
                <img 
                  src={getDefaultThumbnail(material.tipo)} 
                  alt={material.titulo} 
                  className="w-full h-full object-cover"
                />
              )}
            </div>
          </div>
          
          {/* Informações do material */}
          <div className="p-6 flex flex-col">
            <div>
              <div className="inline-block px-2 py-1 bg-vermelho-escuro text-white text-xs rounded mb-3">
                {material.tipo === 'vídeo' ? 'Vídeo' : 
                 material.tipo === 'livro' ? 'Livro Digital' : 'Revista Digital'}
              </div>
              
              <h2 className="text-2xl font-bold text-white mb-2 font-raleway">{material.titulo}</h2>
              
              <div className="mt-4 mb-8">
                <h3 className="text-sm font-medium text-gray-400 mb-1">Descrição</h3>
                <p className="text-gray-300 text-sm whitespace-pre-line">{material.descricao}</p>
              </div>
            </div>
            
            <div className="mt-auto">
              <button
                onClick={onPlayContent}
                className="w-full py-3 px-4 bg-vermelho-claro hover:bg-vermelho-claro/90 text-white rounded-md transition-colors font-medium flex items-center justify-center"
              >
                <FiPlay className="mr-2" />
                Acessar Conteúdo
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaterialViewModal; 